'use client';

import { createContext, useCallback, useContext, useState, ReactNode } from 'react';
import { useRouter } from 'next/navigation';

interface ScheduleDemoContextType {
  shouldShow: boolean;
  showDemoScheduler: () => void;
}

const ScheduleDemoContext = createContext<ScheduleDemoContextType | undefined>(undefined);

export function ScheduleDemoProvider({ children }: { children: ReactNode }) {
  const [shouldShow, setShouldShow] = useState(false);

  const showDemoScheduler = () => {
    setShouldShow(true);
  };

  return (
    <ScheduleDemoContext.Provider value={{ shouldShow, showDemoScheduler }}>
      {children}
    </ScheduleDemoContext.Provider>
  );
}

export function useScheduleDemo() {
  const context = useContext(ScheduleDemoContext);
  if (context === undefined) {
    throw new Error('useScheduleDemo must be used within a ScheduleDemoProvider');
  }
  return context;
}

/** Where the scheduler lives when the current page has no contact section. */
export const CONTACT_PAGE = '/contact#contact';

/**
 * One "book a demo" action for the header, footer and CTAs: open the scheduler
 * and bring the contact section into view. On a page that has no contact
 * section (blog posts, guides, legal pages, About) it navigates client-side to
 * the Contact page instead; the provider lives in the root layout, so the
 * scheduler is already open when that page mounts.
 */
export function useBookDemo() {
  const { showDemoScheduler } = useScheduleDemo();
  const router = useRouter();

  return useCallback(() => {
    showDemoScheduler();
    const section = document.getElementById('contact');
    if (section) {
      section.scrollIntoView();
      return;
    }
    router.push(CONTACT_PAGE);
  }, [router, showDemoScheduler]);
}
